import type { CoachClient } from '../screens/CoachScreen';
import type { PeopleClient } from '../screens/PeopleScreen';
import type { ResultsClient } from '../screens/ResultsScreen';
import type { CoachSource, CoachTopic } from '../types/coach';
import type { OwnProfile, PeopleResponse, Person } from '../types/people';
import type { ResultsResponse } from '../types/results';
import { coachClientFor } from './coachClient';

/**
 * One call to the SoulThread API for the signed-in person: a path under /api, and the method and
 * JSON body; resolves to the JSON answer. The web and the phone each sign it their own way.
 */
export type ApiRequest = <T>(
  path: string,
  init?: { method?: string; body?: unknown },
  failure?: string
) => Promise<T>;

/** A chat with SoulThread over HTTP, about one topic. */
export function httpCoachClient(
  request: ApiRequest,
  topic: CoachTopic,
  openSource?: (source: CoachSource) => void
): CoachClient {
  const coach = <T>(path: string, method = 'GET', body?: unknown) =>
    request<T>('/api/coach' + path, { method, body });
  return coachClientFor(
    topic,
    {
      load: (key) => coach(key === 'main' ? '' : '?topic=' + encodeURIComponent(key)),
      ask: (question, requestId, key) => coach('/ask', 'POST', { question, requestId, topic: key }),
      main: {
        saveMemory: (text, requestId) => coach('/memories', 'POST', { text, requestId }),
        changeMemory: (memory, text) =>
          coach('/memories/' + encodeURIComponent(memory.id), text === null ? 'DELETE' : 'PUT', {
            version: memory.version,
            text: text ?? undefined,
          }),
        savePreferences: (preferences) => coach('/preferences', 'PUT', preferences),
        // Check-ins are sent by the person's Mac, so one must be connected that can send them.
        prepareCheckins: async () => {
          const status = await request<{
            devices?: { capabilities: { selfCheckins?: boolean } }[];
          }>('/api/realtime/status', {}, 'Couldn’t check your connected Mac. Please try again.');
          if (!status.devices?.some((device) => device.capabilities.selfCheckins))
            throw new Error(
              'Open Ask in SoulThread for Mac and choose “Keep me posted” there to allow messages from your device.'
            );
        },
      },
    },
    openSource
  );
}

/** Results over HTTP; checkout opens however the platform opens a web page. */
export const httpResultsClient = (
  request: ApiRequest,
  {
    openCheckout,
    openSource,
  }: { openCheckout: (url: string) => void; openSource?: (source: CoachSource) => void }
): ResultsClient => ({
  load: () => request<ResultsResponse>('/api/results', {}, 'Couldn’t load your results.'),
  analyze: async () => {
    await request('/api/workspace/analysis', { method: 'POST' });
  },
  unlock: async () => {
    const { url } = await request<{ url: string }>('/api/results/unlock', { method: 'POST' });
    openCheckout(url);
  },
  retry: async () => {
    await request('/api/results/retry', { method: 'POST' });
  },
  chat: (topic) => httpCoachClient(request, topic, openSource),
});

/** The people someone writes to, over HTTP. */
export const httpPeopleClient = (
  request: ApiRequest,
  openSource?: (source: CoachSource) => void
): PeopleClient => ({
  load: (offset) =>
    request<PeopleResponse>(
      '/api/people' + (offset ? `?offset=${offset}` : ''),
      {},
      'Couldn’t load the people you talk with.'
    ),
  person: (edgeId) =>
    request<Person>('/api/people/' + encodeURIComponent(edgeId), {}, 'Not found.'),
  rename: (edgeId, name) =>
    request<Person>(`/api/people/${encodeURIComponent(edgeId)}/name`, {
      method: 'PUT',
      body: { name },
    }),
  chat: (topic) => httpCoachClient(request, topic, openSource),
});

/** The signed-in person's own photo, for the corner of the app. */
export const loadOwnProfile = (request: ApiRequest) => request<OwnProfile>('/api/people/me');
